import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import { connect, useDispatch } from 'react-redux';
import 'react-flags-select/css/react-flags-select.css';
import { createForm } from 'rc-form';
import { Link, NavLink } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import Tooltip from '@material-ui/core/Tooltip';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import ReactFlagsSelect from 'react-flags-select';
import MenuIcon from '@material-ui/icons/Menu';
import AccountCircle from '@material-ui/icons/AccountCircle';
import NotificationsIcon from '@material-ui/icons/NotificationsRounded';
import ExitToAppRounded from '@material-ui/icons/ExitToAppRounded';
import { push } from 'connected-react-router';

import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';

import { authLogoutRequest } from 'yeself-client-common/security/SecurityAction';

import { selectAccountUrl } from 'yeself-client-common/security/SecuritySelector';
import { selectFeatureFlags } from 'yeself-client-common/featureFlags/FeatureFlagsSelector';

import Feature from 'yeself-client-common/featureFlags/components/Feature';

import EnvironmentConfiguration from 'yeself-client-common/EnvironmentConfiguration';

import { fetchMemberOrganizationsList } from 'Console/data/organizations/OrganizationsAction';

import { selectApplicationMetaDataLanguageUsedLocales } from 'Console/data/applicationDetail/settings/SettingsSelector';
import { selectMemberOrganizationsList } from 'Console/data/organizations/OrganizationSelector';

import FormSelect from 'Console/components/common/form/FormSelect';
import AnalyticsButton from 'Console/components/common/analyticsButton/AnalyticsButton';

import { ROUTES, ACCOUNT_DETAIL_ROUTE, ANALYTICS_ROUTE } from 'Console/constants/routes';
import { changeConsoleLanguage, changeContentLanguage } from 'Console/data/app/actions';
import {
    selectAvailableLanguages,
    selectConsoleLanguageCode,
    selectContentLanguage,
} from 'Console/data/app/selectors';
import { mapLanguageCodeToCountryCode, mapCountryCodeToLanguage } from 'Console/utils/translationLoader';
import { selectUserEmail } from 'Console/data/security/selectors';
import logoWithName from 'Console/assets/images/nexthink-whiteout.png';
import { MiUiTheme } from 'Console/utils/styleUtils';

const styles = (theme) => ({
    grow: {
        flexGrow: 1,
    },
    appBar: {
        'background-color': theme.palette.tertiary.main,
    },
    menuButton: {
        marginRight: -12,
        marginLeft: 20,
    },
    label: {
        'font-size': 12,
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    sectionDesktop: {
        display: 'none',
        marginLeft: 135,
        [theme.breakpoints.up('md')]: {
            display: 'flex',
        },
    },
    navlink: {
        textDecoration: 'none',
        color: theme.palette.textPrimary.color,
    },
    navlinkActive: {
        color: 'rgba(255, 255, 255, 1)',
    },
    languageLabel: {
        color: theme.palette.textPrimary.color,
        'font-size': theme.typography.pxToRem(14),
        'font-weight': 500,
    },
    contentLanguageSelectorWrapper: {
        marginLeft: 20,
        marginRight: 20,
    },
    selectRoot: {
        color: theme.palette.textPrimary.color,
        'text-transform': 'capitalize',
        'font-size': theme.typography.pxToRem(14),
        'font-weight': 500,
        '& > div:before, & > div:after': {
            borderBottom: `2px solid ${theme.palette.textPrimary.color}`,
        },
        '& > div:hover:not([disabled]):not([focused]):not([error]):before': {
            borderBottom: `2px solid ${theme.palette.textPrimary.color} !important`,
        },
    },
    icon: {
        color: theme.palette.textPrimary.color,
    },
    countryFlags: {
        '& ~ span[class="arrow-down "]': {
            color: '#fff',
        },
    },
});

const Topbar = ({
    classes,
    handleDrawerToggle,
    authLogoutRequest,
    changeConsoleLanguage,
    changeContentLanguage,
    availableLocales,
    consoleLanguageCode,
    contentLanguage,
    accountId,
    accountUrl,
    form,
    usedLocales,
    memberOrganizationsList,
    push,
    fetchMemberOrganizationsList,
    featureFlags,
}) => {
    const dispatch = useDispatch();

    useEffect(() => {
        if (featureFlags[FEATURE_FLAG_NAMES.ANALYTICS_CONSOLE]) {
            fetchMemberOrganizationsList();
        }
    }, [fetchMemberOrganizationsList, featureFlags]);

    const onOpenMenu = () => handleDrawerToggle(true);

    const handleChangeConsoleLanguage = (countryCode) => {
        const selectedLocale = availableLocales.find(
            (locale) => locale.code.toLowerCase() === mapCountryCodeToLanguage(countryCode).toLowerCase()
        );
        changeConsoleLanguage(selectedLocale);
    };

    const handleChangeContentLanguage = (event) => {
        changeContentLanguage({
            code: event.target.value.id,
            label: event.target.value.name,
        });
    };

    const powerbiNavLink = EnvironmentConfiguration.ANALYTICS_URL;

    const onOrganizationClick = (organizationId) => {
        push(`${ANALYTICS_ROUTE}${organizationId}`);
    };

    return (
        <AppBar position="fixed" className={classes.appBar}>
            <Grid container className={classes.grow} alignItems="center" justify="center">
                <Grid item xs={12}>
                    <Grid container alignItems="center" justify="center">
                        <Grid item xs={12}>
                            <Toolbar variant="dense">
                                <Link to={ROUTES.APPLICATIONS} style={{ display: 'flex' }}>
                                    <img
                                        id="yeself-topbar-logo"
                                        src={logoWithName}
                                        alt="logo"
                                        height="28"
                                        style={{ marginRight: 10 }}
                                    />
                                </Link>
                                <div className={classes.sectionDesktop} style={{ alignItems: 'center' }}>
                                    <NavLink
                                        id="yeself-navLink"
                                        className={classes.navlink}
                                        activeStyle={{ color: MiUiTheme.palette.textPrimary.color }}
                                        to={ROUTES.BASE}
                                        exact
                                    >
                                        <Button
                                            id="yeself-applications-button"
                                            size="small"
                                            color="inherit"
                                        >
                                            <span className={classes.label}>
                                                <FormattedMessage
                                                    id="Navigation.applications"
                                                    defaultMessage="Spaces"
                                                />
                                            </span>
                                        </Button>
                                    </NavLink>
                                    <Feature name={FEATURE_FLAG_NAMES.ANALYTICS_CONSOLE}>
                                        <AnalyticsButton
                                            onOrganizationItemClick={onOrganizationClick}
                                            organizations={memberOrganizationsList}
                                        />
                                    </Feature>
                                </div>
                                <div className={classes.grow} />
                                <div className={classes.sectionDesktop}>
                                    <Grid
                                        id="yeself-content-language-label"
                                        container
                                        alignItems="center"
                                        justify="flex-end"
                                    >
                                        <Feature name={FEATURE_FLAG_NAMES.CONTENT_LANGUAGE}>
                                            <Typography
                                                variant="body1"
                                                className={classes.languageLabel}
                                            >
                                                <FormattedMessage
                                                    id="Guides.contentLanguage"
                                                    defaultMessage="Content language"
                                                />
                                            </Typography>
                                            <div
                                                id="yeself-content-language-selector-wrapper"
                                                className={classes.contentLanguageSelectorWrapper}
                                            >
                                                <FormSelect
                                                    isRequired
                                                    form={form}
                                                    fullWidth={false}
                                                    formItemKey="contextHelp.locale"
                                                    optionList={usedLocales.map((locale) => ({
                                                        id: locale.code,
                                                        name: locale.label,
                                                    }))}
                                                    initialValue={{
                                                        id: contentLanguage.code,
                                                        name: contentLanguage.label,
                                                    }}
                                                    onChange={handleChangeContentLanguage}
                                                    classes={{
                                                        selectRoot: classes.selectRoot,
                                                        icon: classes.icon,
                                                    }}
                                                />
                                            </div>
                                        </Feature>

                                        <Feature name={FEATURE_FLAG_NAMES.CONSOLE_LANGUAGE}>
                                            <ReactFlagsSelect
                                                className={classes.countryFlags}
                                                countries={availableLocales.map((locale) =>
                                                    mapLanguageCodeToCountryCode(
                                                        locale.code
                                                    ).toUpperCase()
                                                )}
                                                defaultCountry={mapLanguageCodeToCountryCode(
                                                    consoleLanguageCode
                                                ).toUpperCase()}
                                                showSelectedLabel={false}
                                                showOptionLabel={false}
                                                selectedSize={14}
                                                onSelect={handleChangeConsoleLanguage}
                                            />
                                        </Feature>
                                    </Grid>

                                    <Feature name={FEATURE_FLAG_NAMES.NOTIFICATIONS}>
                                        <IconButton color="inherit">
                                            <NavLink
                                                id="yeself-budge-link"
                                                className={classes.navlinkActive}
                                                to={`${ACCOUNT_DETAIL_ROUTE}${accountId}/notifications`}
                                                exact
                                            >
                                                <Badge
                                                    className="yeself-badge"
                                                    badgeContent={4}
                                                    color="secondary"
                                                >
                                                    <NotificationsIcon />
                                                </Badge>
                                            </NavLink>
                                        </IconButton>
                                    </Feature>

                                    <Feature name={FEATURE_FLAG_NAMES.PROFILE}>
                                        <Tooltip
                                            id="yeself-profile-tooltip"
                                            title={<FormattedMessage id="Navigation.user.profile" />}
                                        >
                                            <IconButton
                                                id="yeself-profile-button"
                                                href={accountUrl}
                                                aria-haspopup="true"
                                                color="inherit"
                                            >
                                                <AccountCircle />
                                            </IconButton>
                                        </Tooltip>
                                    </Feature>

                                    <Tooltip
                                        id="yeself-logout-tooltip"
                                        title={<FormattedMessage id="Navigation.logout" />}
                                    >
                                        <IconButton
                                            id="yeself-logout-button"
                                            aria-haspopup="true"
                                            color="inherit"
                                            className={classes.navlink}
                                            onClick={authLogoutRequest}
                                        >
                                            <ExitToAppRounded />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                                <Hidden mdUp implementation="css">
                                    <IconButton
                                        color="inherit"
                                        aria-label="Open drawer"
                                        onClick={onOpenMenu}
                                        className={classes.menuButton}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                </Hidden>
                            </Toolbar>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </AppBar>
    );
};

export default connect(
    (state) => ({
        availableLocales: selectAvailableLanguages(state),
        consoleLanguageCode: selectConsoleLanguageCode(state),
        contentLanguage: selectContentLanguage(state),
        accountUrl: selectAccountUrl(state),
        accountId: selectUserEmail(state),
        usedLocales: selectApplicationMetaDataLanguageUsedLocales(state),
        memberOrganizationsList: selectMemberOrganizationsList(state),
        featureFlags: selectFeatureFlags(state),
    }),
    {
        authLogoutRequest,
        changeConsoleLanguage,
        changeContentLanguage,
        push,
        fetchMemberOrganizationsList,
    }
)(createForm()(withStyles(styles, { withTheme: true })(Topbar)));
